import { isStagingHost } from './staging-host.js';

const ELEMENT_ID = 'dmre-env-banner';
const HEIGHT_VARIABLE = '--dmre-env-banner-height';
const STYLE_ID = 'dmre-env-banner-style';
const DEFAULT_LABEL = 'Staging environment — not production data';
const BACKGROUND = '#FDB022';
const FOREGROUND = '#1D2939';
const HEIGHT = '22px';

const STYLE = `
#${ELEMENT_ID} {
  position: sticky;
  top: 0;
  z-index: 2147483000;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4em;
  box-sizing: border-box;
  width: 100%;
  height: ${HEIGHT};
  padding: 0 8px;
  background: ${BACKGROUND};
  color: ${FOREGROUND};
  font: 600 11px/1 ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
@media print {
  #${ELEMENT_ID} {
    display: none;
  }
}
`;

function insertStyle(doc) {
  if (doc.getElementById(STYLE_ID)) {
    return;
  }
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = STYLE;
  doc.head.appendChild(style);
}

function insertBanner(doc, label) {
  const existing = doc.getElementById(ELEMENT_ID);
  if (existing) {
    return existing;
  }
  const banner = doc.createElement('div');
  banner.id = ELEMENT_ID;
  banner.setAttribute('role', 'status');
  banner.textContent = label;
  doc.body.insertBefore(banner, doc.body.firstChild);
  return banner;
}

/**
 * Mounts the environment banner as the first child of `body` when the host is
 * a staging host. The banner sits in the document flow and sticks to the top,
 * so it covers no application content.
 *
 * Returns a function that removes the banner, or null when nothing mounted.
 */
export function mountStagingBanner(options = {}) {
  const doc = options.document ?? globalThis.document;
  if (!doc?.body) {
    return null;
  }
  const host = options.host ?? globalThis.location?.host;
  if (!isStagingHost(host)) {
    return null;
  }
  insertStyle(doc);
  const banner = insertBanner(doc, options.label ?? DEFAULT_LABEL);
  doc.documentElement.style.setProperty(HEIGHT_VARIABLE, HEIGHT);
  return () => {
    banner.remove();
    doc.getElementById(STYLE_ID)?.remove();
    doc.documentElement.style.removeProperty(HEIGHT_VARIABLE);
  };
}
